import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FlaskConical, Pencil, Plus, Search, Trash2 } from 'lucide-react';
import { Heading } from '../../layout';
import { FormDrawer } from '../../forms';
import { useUIColors } from '../../../hooks/useTheme';
import { useSectionAccess } from '../../../hooks/useSectionAccess';
import { additivesCatalogAPI } from '../../../services/additivesCatalogAPI';

const emptyProduct = () => ({
  code: '',
  name: '',
  family: '',
  format: '',
  dose: '',
  summary: '',
  tags_text: '',
  applications_text: '',
  properties_text: '',
});

const toForm = (item) => ({
  code: item.code || '',
  name: item.name || '',
  family: item.family || '',
  format: item.format || '',
  dose: item.dose || '',
  summary: item.summary || '',
  tags_text: (item.tags || []).join(', '),
  applications_text: (item.applications || []).join(', '),
  properties_text: (item.properties || [])
    .map((prop) => `${prop.label || ''}: ${prop.value || ''}`.replace(/^: /, ''))
    .join('\n'),
});

const splitList = (value) =>
  String(value || '')
    .split(/[,;\n]/)
    .map((part) => part.trim())
    .filter(Boolean);

const parseProperties = (value) =>
  String(value || '')
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean)
    .map((line) => {
      const index = line.indexOf(':');
      if (index < 0) return { label: line, value: '' };
      return { label: line.slice(0, index).trim(), value: line.slice(index + 1).trim() };
    });

const toPayload = (form) => ({
  code: form.code,
  name: form.name,
  family: form.family,
  format: form.format,
  dose: form.dose,
  summary: form.summary,
  tags: splitList(form.tags_text),
  applications: splitList(form.applications_text),
  properties: parseProperties(form.properties_text),
});

const PhenoxProductsWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const access = useSectionAccess();
  const [query, setQuery] = useState('');
  const [family, setFamily] = useState('Todas');
  const [items, setItems] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState(emptyProduct());
  const [editingId, setEditingId] = useState(null);
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      setIsLoading(true);
      setError('');
      const rows = await additivesCatalogAPI.listProducts();
      setItems(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setItems([]);
      setError(err?.message || 'No se pudieron cargar los productos.');
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const families = useMemo(() => {
    const values = Array.from(new Set(items.map((item) => item.family).filter(Boolean)));
    return ['Todas', ...values];
  }, [items]);

  const products = useMemo(() => {
    const q = query.trim().toLowerCase();
    return items.filter((item) => {
      if (family !== 'Todas' && item.family !== family) return false;
      if (!q) return true;
      return [item.name, item.code, item.summary, item.family, ...(item.tags || [])]
        .join(' ')
        .toLowerCase()
        .includes(q);
    });
  }, [items, query, family]);

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyProduct());
    setFormError('');
    setFormOpen(true);
  };

  const openEdit = (item) => {
    setEditingId(item.id);
    setForm(toForm(item));
    setFormError('');
    setFormOpen(true);
  };

  const saveForm = async () => {
    try {
      setIsSaving(true);
      setFormError('');
      const payload = toPayload(form);
      if (editingId) {
        await additivesCatalogAPI.updateProduct(editingId, payload);
      } else {
        await additivesCatalogAPI.createProduct(payload);
      }
      setFormOpen(false);
      await load();
    } catch (err) {
      setFormError(err?.message || 'No se pudo guardar el producto.');
    } finally {
      setIsSaving(false);
    }
  };

  const removeItem = async (item) => {
    if (!window.confirm(`¿Eliminar “${item.name}”?`)) return;
    try {
      await additivesCatalogAPI.removeProduct(item.id);
      await load();
    } catch (err) {
      window.alert(err?.message || 'No se pudo eliminar el producto.');
    }
  };

  const primaryBtn = {
    height: 34,
    borderRadius: 8,
    border: 'none',
    background: ui.interactive || '#16a34a',
    color: ui.textOnAccent || '#fff',
    padding: '0 12px',
    cursor: 'pointer',
    fontSize: 13,
    fontWeight: 600,
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
  };
  const ghostBtn = {
    height: 28,
    borderRadius: 8,
    border: `1px solid ${ui.border}`,
    background: 'transparent',
    color: ui.text,
    padding: '0 8px',
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
  };

  const formSections = [
    {
      key: 'main',
      title: 'Ficha del producto',
      defaultOpen: true,
      fields: [
        { key: 'code', label: 'Código' },
        { key: 'name', label: 'Nombre' },
        { key: 'family', label: 'Familia' },
        { key: 'format', label: 'Formato' },
        { key: 'dose', label: 'Dosificación', fullWidth: true },
        { key: 'summary', label: 'Descripción', type: 'textarea', rows: 3, fullWidth: true },
        { key: 'tags_text', label: 'Etiquetas (separadas por coma)', fullWidth: true },
        { key: 'applications_text', label: 'Aplicaciones (separadas por coma)', fullWidth: true },
        {
          key: 'properties_text',
          label: 'Propiedades (una por línea: Etiqueta: valor)',
          type: 'textarea',
          rows: 6,
          fullWidth: true,
        },
      ],
    },
  ];

  return (
    <div style={{ display: 'grid', gap: 16 }}>
      <div>
        <Heading level={2} isDarkMode={isDarkMode}>
          <FlaskConical size={22} style={{ marginRight: 8, verticalAlign: 'middle' }} />
          Productos PhenoX®
        </Heading>
        <p style={{ margin: '6px 0 0', fontSize: 13, color: ui.muted }}>
          Aditivos inteligentes biobasados para recubrimientos: anticorrosivos, antimicrobianos y nano.
        </p>
      </div>

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
        <label style={{ position: 'relative', flex: '1 1 240px', minWidth: 200 }}>
          <Search size={14} style={{ position: 'absolute', left: 10, top: 10, color: ui.muted }} />
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Buscar producto, código o propiedad…"
            style={{
              width: '100%',
              height: 34,
              padding: '0 10px 0 30px',
              borderRadius: 8,
              border: `1px solid ${ui.inputBorder || ui.border}`,
              background: ui.inputBg || ui.panel,
              color: ui.inputText || ui.text,
              fontSize: 13,
              boxSizing: 'border-box',
            }}
          />
        </label>
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', flex: 1, alignItems: 'center' }}>
          {families.map((item) => {
            const active = family === item;
            return (
              <button
                key={item}
                type="button"
                onClick={() => setFamily(item)}
                style={{
                  height: 30,
                  padding: '0 12px',
                  borderRadius: 999,
                  border: `1px solid ${active ? ui.interactive || '#16a34a' : ui.border}`,
                  background: active ? ui.interactive || '#16a34a' : 'transparent',
                  color: active ? ui.textOnAccent || '#fff' : ui.text,
                  fontSize: 12,
                  fontWeight: 600,
                  cursor: 'pointer',
                }}
              >
                {item}
              </button>
            );
          })}
        </div>
        {access.canCreate ? (
          <button type="button" onClick={openCreate} style={primaryBtn}>
            <Plus size={14} />
            Nuevo producto
          </button>
        ) : null}
      </div>

      {error ? <div style={{ fontSize: 13, color: '#dc2626' }}>{error}</div> : null}
      {isLoading ? <div style={{ fontSize: 13, color: ui.muted }}>Cargando productos…</div> : null}

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))',
          gap: 14,
        }}
      >
        {products.map((product) => (
          <article
            key={product.id}
            style={{
              border: `1px solid ${ui.border}`,
              borderRadius: 14,
              background: ui.panel,
              padding: 16,
              display: 'grid',
              gap: 12,
              minWidth: 0,
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', gap: 8, alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted, letterSpacing: '0.04em' }}>
                  {product.code}
                </div>
                <h3 style={{ margin: '4px 0 0', fontSize: 16, color: ui.text }}>{product.name}</h3>
              </div>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  padding: '4px 8px',
                  borderRadius: 999,
                  background: isDarkMode ? '#14532d' : '#dcfce7',
                  color: isDarkMode ? '#bbf7d0' : '#166534',
                  whiteSpace: 'nowrap',
                }}
              >
                {product.family || 'Sin familia'}
              </span>
            </div>

            <p style={{ margin: 0, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>{product.summary}</p>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(product.tags || []).map((tag) => (
                <span
                  key={tag}
                  style={{
                    fontSize: 11,
                    padding: '3px 8px',
                    borderRadius: 999,
                    border: `1px solid ${ui.border}`,
                    color: ui.text,
                  }}
                >
                  {tag}
                </span>
              ))}
            </div>

            <dl
              style={{
                margin: 0,
                display: 'grid',
                gap: 8,
                borderTop: `1px solid ${ui.border}`,
                paddingTop: 12,
              }}
            >
              {(product.properties || []).map((prop) => (
                <div
                  key={`${prop.label}-${prop.value}`}
                  style={{ display: 'grid', gridTemplateColumns: '110px 1fr', gap: 8, fontSize: 12 }}
                >
                  <dt style={{ color: ui.muted, margin: 0 }}>{prop.label}</dt>
                  <dd style={{ margin: 0, color: ui.text, fontWeight: 600 }}>{prop.value}</dd>
                </div>
              ))}
            </dl>

            <div>
              <div style={{ fontSize: 11, fontWeight: 700, color: ui.muted, marginBottom: 6 }}>Aplicaciones</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(product.applications || []).map((app) => (
                  <span key={app} style={{ fontSize: 11, color: ui.text }}>
                    · {app}
                  </span>
                ))}
              </div>
            </div>

            {access.canUpdate || access.canDelete ? (
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                {access.canUpdate ? (
                  <button type="button" onClick={() => openEdit(product)} style={ghostBtn} title="Editar">
                    <Pencil size={13} />
                  </button>
                ) : null}
                {access.canDelete ? (
                  <button type="button" onClick={() => removeItem(product)} style={ghostBtn} title="Eliminar">
                    <Trash2 size={13} />
                  </button>
                ) : null}
              </div>
            ) : null}
          </article>
        ))}
      </div>

      {!isLoading && products.length === 0 ? (
        <div style={{ fontSize: 13, color: ui.muted, padding: 12 }}>
          No hay productos. Crea el primero con “Nuevo producto”.
        </div>
      ) : null}

      <FormDrawer
        isOpen={formOpen}
        onClose={() => !isSaving && setFormOpen(false)}
        onSave={saveForm}
        isSaving={isSaving}
        isDarkMode={isDarkMode}
        title={editingId ? 'Editar producto' : 'Nuevo producto'}
        subtitle="PhenoX®"
        sections={formSections}
        formData={form}
        onChange={(key, value) => setForm((prev) => ({ ...prev, [key]: value }))}
        formError={formError}
        saveLabel={editingId ? 'Guardar' : 'Crear'}
        readOnly={editingId ? !access.canUpdate : !access.canCreate}
        width={560}
      />
    </div>
  );
};

export default PhenoxProductsWorkspace;
